import { useTranslation } from 'react-i18next';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import toolbarStyles from './GanttEditToolbar.module.css';
import cardStyles from './ReviewChangesModal.module.css';

interface ReviewChangesModalProps {
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  changeSummary?: ChangeSummary | null;
}

function formatDelta(value: number): string {
  if (value > 0) return `+${String(value)}`;
  return String(value);
}

function getFootnoteClass(percent: number): string {
  if (percent >= 0) return cardStyles.footnotePositive;
  if (Math.abs(percent) <= 10) return cardStyles.footnoteWarning;
  return cardStyles.footnoteDanger;
}

function formatPercent(percent: number): string {
  return `${percent >= 0 ? '+' : ''}${String(percent)}%`;
}

export const ReviewChangesModal = ({
  onClose,
  onConfirm,
  changeSummary
}: ReviewChangesModalProps) => {
  const { t } = useTranslation();

  const capacityDelta = changeSummary
    ? changeSummary.currentTotalCapacity - changeSummary.serverTotalCapacity
    : 0;

  const addedEntries = changeSummary ? Object.entries(changeSummary.treatmentPlansAdded) : [];
  const deletedEntries = changeSummary ? Object.entries(changeSummary.treatmentPlansDeleted) : [];
  const totalAdded = addedEntries.reduce((sum, [, count]) => sum + count, 0);
  const totalDeleted = deletedEntries.reduce((sum, [, count]) => sum + count, 0);
  const treatmentTypeDelta = totalAdded - totalDeleted;

  const capacityPercent =
    changeSummary && changeSummary.serverTotalCapacity > 0
      ? Math.round((capacityDelta / changeSummary.serverTotalCapacity) * 100)
      : 0;

  const planPercent =
    changeSummary && changeSummary.serverTotalPlans > 0
      ? Math.round((treatmentTypeDelta / changeSummary.serverTotalPlans) * 100)
      : 0;

  return (
    <Modal
      title={t('save-modal-title')}
      onClose={onClose}
      footer={
        <div className={toolbarStyles.modalFooter}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            {t('save-modal-confirm')}
          </Button>
        </div>
      }
    >
      <div className={toolbarStyles.warningRow}>
        <WarningIcon className={toolbarStyles.warningIcon} />
        <span>{t('save-modal-algorithm-warning')}</span>
      </div>
      {changeSummary && (
        <div className={cardStyles.summaryRow}>
          {/* Economy card removed (REH-221): figures were hard-coded placeholders, not real data. */}
          <div className={cardStyles.summaryCard}>
            <div className={cardStyles.summaryCardHeader}>
              <div className={cardStyles.summaryCardTitle}>{t('save-modal-patients-title')}</div>
            </div>
            <div className={cardStyles.summaryCardBody}>
              <div className={cardStyles.summaryCardValue}>{formatDelta(capacityDelta)}</div>
              <div className={cardStyles.summaryCardDetail}>
                {t('save-modal-patients-detail', {
                  from: changeSummary.serverTotalCapacity,
                  to: changeSummary.currentTotalCapacity
                })}
              </div>
              <div
                className={`${cardStyles.summaryCardFootnote} ${getFootnoteClass(capacityPercent)}`}
              >
                <span className={cardStyles.summaryCardFootnoteLabel}>
                  {t('save-modal-change-label')}
                </span>
                <span className={cardStyles.summaryCardFootnoteValue}>
                  {formatPercent(capacityPercent)}
                </span>
              </div>
            </div>
          </div>

          <div className={cardStyles.summaryCard}>
            <div className={cardStyles.summaryCardHeader}>
              <div className={cardStyles.summaryCardTitle}>
                {t('save-modal-treatment-types-title')}
              </div>
            </div>
            <div className={cardStyles.summaryCardBody}>
              <div className={cardStyles.summaryCardValue}>{formatDelta(treatmentTypeDelta)}</div>
              {addedEntries.map(([name, count]) => (
                <div key={`added-${name}`} className={cardStyles.summaryCardDetailRow}>
                  <span>{name}</span>
                  <span className={cardStyles.summaryCardDetailValue}>{count}</span>
                </div>
              ))}
              {deletedEntries.map(([name, count]) => (
                <div key={`deleted-${name}`} className={cardStyles.summaryCardDetailRow}>
                  <span>{name}</span>
                  <span className={cardStyles.summaryCardDetailValue}>-{count}</span>
                </div>
              ))}
              <div className={`${cardStyles.summaryCardFootnote} ${getFootnoteClass(planPercent)}`}>
                <span className={cardStyles.summaryCardFootnoteLabel}>
                  {t('save-modal-change-label')}
                </span>
                <span className={cardStyles.summaryCardFootnoteValue}>
                  {formatPercent(planPercent)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
